import React from 'react';
import BooleanRadioButtons from '../components/BooleanRadioButtons';
import Card from '../components/card';
import DatePicker from '../components/date-picker';
import style from '../styles/form';
import { LoanCard } from './card-lenses';

const titleText = `What is the approximate close date on your new home?`;

const tooFar = 1000 * 60 * 60 * 24 * 90; // 90 days.

/**
 * Checks if the timespan between now and the close date is greater
 *   than the `tooFar` constant.
 *
 * @param {*} closeDate - as number
 */
const isTooFar = (closeDate) => closeDate && closeDate - Date.now() > tooFar;

/**
 * Return any errors associated with the current close date.
 *
 * @param {*} closeDate - as number
 */
const getCloseDateError = (closeDate) =>
  closeDate && closeDate > Date.now() ? '' : 'Close date must be in the future';

/**
 * Check if the state of the `isNewConstruction` field is valid
 *   given your current close date.
 *
 * Returns true if the close date is within `tooFar` or if it is
 *   selected as true or false.
 *
 * @param {*} closeDate - as number
 * @param {*} isNewConstruction
 */
export const isNewConstructionValid = (closeDate, isNewConstruction) => {
  return !isTooFar(closeDate) || isNewConstruction != null;
};

/**
 * Check if a consult should reject due to a new construction being too
 *   soon in the future.
 *
 * Returns true if the close date is within `tooFar` and `isNewConstruction`
 *   is true.
 *
 * @param {*} closeDate - as number
 * @param {*} isNewConstruction
 */
export const isNewConstructionTooSoon = (closeDate, isNewConstruction) => {
  return isNewConstruction && isTooFar(closeDate);
};

const CloseDate = ({ model, onChange, onSubmit, ...cardProps }) => {
  const now = new Date();
  const minDate = new Date(now.getFullYear(), 0, 1);
  const maxDate = new Date(now.getFullYear() + 2, 11, 31);

  const onConstructionChange = onChange.bind(null, 'isNewConstruction');

  /**
   * Strip `isNewConstruction` field from model if it's not needed.
   */
  const wrappedSubmit = () => {
    if (model.get('closeDate')) {
      onConstructionChange(null);
    }
    onSubmit();
  };

  const farOutState = isTooFar(model.get('closeDate'));

  // We now also always want to ask if a user is working with a builder if they are in Texas.
  const isTexas = model.get('loanState') === 'TX';

  return (
    <Card title={titleText} onSubmit={wrappedSubmit} {...cardProps}>
      <DatePicker
        value={model.get('closeDate')}
        onChange={onChange.bind(null, 'closeDate')}
        style={style.fullWidth}
        validate={getCloseDateError}
        hintText={{ month: 'Closing Month', day: 'Closing Day', year: 'Closing Year' }}
        minDate={minDate}
        maxDate={maxDate}
        sortYearAscending
      />
      {farOutState || isTexas ? (
        <div>
          <br />
          <br />
          <p>Are you working with a builder that has an affiliated mortgage company?</p>
          <BooleanRadioButtons
            name="hasAgent"
            value={model.get('isNewConstruction')}
            onChange={onConstructionChange}
          />
        </div>
      ) : null}
    </Card>
  );
};

CloseDate.id = 'CloseDate';

/**
 * Ensures there is no error on `closeDate` and the close date is
 *   within the set range or you've chosen an `isNewConstruction` state.
 */
CloseDate.completed = ({ model }) => {
  const closeDate = model.get('closeDate');
  return (
    getCloseDateError(closeDate) === '' &&
    isNewConstructionValid(closeDate, model.get('isNewConstruction'))
  );
};

export default LoanCard(CloseDate);



// WEBPACK FOOTER //
// ./src/cards/close-date.js